/**
 * Feature Tour Store
 *
 * Manages the onboarding feature tour state.
 * Only `completedTours` is persisted to localStorage — all runtime state is ephemeral.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface FeatureTourState {
  // Persisted
  completedTours: Record<string, boolean>;

  // Ephemeral
  isActive: boolean;
  currentTourId: string | null;
  currentStepIndex: number;
}

interface FeatureTourActions {
  startTour: (tourId: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  completeTour: () => void;
  dismissTour: () => void;
  isTourCompleted: (tourId: string) => boolean;
}

export const useFeatureTourStore = create<FeatureTourState & FeatureTourActions>()(
  persist(
    (set, get) => ({
      // Persisted state
      completedTours: {},

      // Ephemeral state
      isActive: false,
      currentTourId: null,
      currentStepIndex: 0,

      startTour: (tourId: string) => {
        set({
          isActive: true,
          currentTourId: tourId,
          currentStepIndex: 0,
        });
      },

      nextStep: () => {
        set((state) => ({
          currentStepIndex: state.currentStepIndex + 1,
        }));
      },

      prevStep: () => {
        set((state) => ({
          currentStepIndex: Math.max(0, state.currentStepIndex - 1),
        }));
      },

      completeTour: () => {
        const { currentTourId, completedTours } = get();
        if (!currentTourId) return;

        const updated = { ...completedTours, [currentTourId]: true };

        // GM tour completion also marks player tour complete
        if (currentTourId === 'gm-intro') {
          updated['player-intro'] = true;
        }

        set({
          isActive: false,
          currentTourId: null,
          currentStepIndex: 0,
          completedTours: updated,
        });
      },

      dismissTour: () => {
        const { currentTourId, completedTours } = get();
        // Dismiss counts as completed (no nagging)
        const updated = currentTourId
          ? { ...completedTours, [currentTourId]: true }
          : completedTours;

        // GM dismiss also marks player tour
        if (currentTourId === 'gm-intro') {
          updated['player-intro'] = true;
        }

        set({
          isActive: false,
          currentTourId: null,
          currentStepIndex: 0,
          completedTours: updated,
        });
      },

      isTourCompleted: (tourId: string) => {
        return !!get().completedTours[tourId];
      },
    }),
    {
      name: 'machina:feature-tour',
      partialize: (state) => ({ completedTours: state.completedTours }),
    }
  )
);
